import { useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

// ─── Slot Group Definitions ───────────────────────────────────────────────────

const split2CtaDef: SlotGroupDefinition = {
  label: 'Split 2 CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Visit the Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const split2ImageDef: SlotGroupDefinition = {
  label: 'Split 2 Section Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[5].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community lounge area',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a striking lifestyle or amenity photo that conveys the experience of living here — a desirable common space, pool, courtyard, or richly styled interior.',
    context:
      'Image on the right side of the home page features split section, paired with copy inviting visitors to imagine the lifestyle; pick something different from the intro hero image.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function HomeFeaturesSplit() {
  const split2Eyebrow = useContentToken('homefeaturessplit.eyebrow');

  const split2Cta = useSlotGroup('homefeaturessplit.cta_link', split2CtaDef);
  const split2Image = useSlotGroup('homefeaturessplit.image', split2ImageDef);

  const galleryImages = useCollectionBinding<GalleryPhoto>('homefeaturessplit.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });

  const carouselPhotos = useMemo(
    () => galleryImages.filter((p) => p.image ?? p.original_url),
    [galleryImages],
  );

  const resolveTileImage = useCallback(
    (slotUrl: string | null | undefined, index: number) => {
      const fallback = galleryImages[index] ?? null;
      return slotUrl || fallback?.image || fallback?.original_url || '';
    },
    [galleryImages],
  );

  // The Split 2 tile binds to `photo_gallery[5]`, but smaller galleries often
  // have no 6th photo. Fall back to an available community photo so the tile
  // always autofills from the photo gallery instead of rendering blank.
  const split2Fallback = carouselPhotos[5] ?? carouselPhotos[2] ?? carouselPhotos[0] ?? null;
  const split2ImageUrl =
    resolveTileImage(split2Image.image, 5) ||
    split2Fallback?.image ||
    split2Fallback?.original_url ||
    '';
  const split2ImageAlt =
    split2Image.alt || split2Fallback?.alt_text || 'Community lounge area';

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[52%_48%] gap-10 lg:gap-8 items-center">
          {/* Left: text */}
          <div className="lg:pr-12">
            <EditableToken
              path="homefeaturessplit.eyebrow"
              defaultValue="Look ahead to upscale living"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text)] mb-4"
            >
              {split2Eyebrow}
            </EditableToken>

            <RichTextContent
              path="homefeaturessplit.heading"
              label="Split 2 Heading"
              defaultValue="<p>Dive into an Immersive Experience</p>"
              as="h2"
              className="font-heading text-4xl sm:text-5xl font-normal text-[var(--vibe-color-text)] mb-6 leading-[1.1]"
            />

            <RichTextContent
              path="homefeaturessplit.body"
              as="p"
              className="font-body text-base text-[var(--vibe-color-text-muted)] leading-relaxed mb-8 max-w-lg"
            />

            <EditableSlotGroup groupPath="homefeaturessplit.cta_link" definition={split2CtaDef}>
              <a
                href={split2Cta.url || '/gallery'}
                target={split2Cta._target || '_self'}
                className="overview-intro-cta inline-flex items-center px-6 py-3 text-sm font-semibold tracking-wide text-[var(--vibe-color-text-on-secondary)] rounded-sm transition-all duration-200"
              >
                {split2Cta.label || 'Visit the Gallery'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Right: image */}
          <EditableSlotGroup
            groupPath="homefeaturessplit.image"
            definition={split2ImageDef}
            className="block lg:pr-8"
          >
            {split2ImageUrl ? (
              <VibesitesImage
                url={split2ImageUrl}
                alt={split2ImageAlt}
                className="w-full rounded-lg object-cover shadow-lg"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-lg bg-[var(--vibe-color-surface)] shadow-lg"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .overview-intro-cta {
          background-color: var(--vibe-color-secondary);
        }
        .overview-intro-cta:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeFeaturesSplit',
  displayName: 'Home Features Split',
  tokens: {
    'homefeaturessplit.eyebrow': {
      label: 'Split 2 Eyebrow',
      type: 'text',
      defaultValue: 'Look ahead to upscale living',
    },
    'homefeaturessplit.heading': {
      label: 'Split 2 Heading',
      type: 'richtext',
      defaultValue: '<p>Dive into an Immersive Experience</p>',
    },
    'homefeaturessplit.body': {
      label: 'Split 2 Body',
      type: 'richtext',
      defaultField: 'property_home_gallery_blurb',
    },
  },
  slotGroups: {
    'homefeaturessplit.cta_link': {
      label: 'Split 2 CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'Visit the Gallery' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'homefeaturessplit.image': {
      label: 'Split 2 Section Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'photo_gallery[5].image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Community lounge area',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 5,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select a striking lifestyle or amenity photo that conveys the experience of living here — a desirable common space, pool, courtyard, or richly styled interior.',
        context:
          'Image on the right side of the home page features split section, paired with copy inviting visitors to imagine the lifestyle; pick something different from the intro hero image.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
  collectionBindings: {
    'homefeaturessplit.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(HomeFeaturesSplit as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeFeaturesSplit;
